import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PORTRAIT =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_session-booking-23/artifacts/tszz0vlg_2025051405_websize.jpg";

const About = () => (
  <main className="bg-[#F6F1EA]" data-testid="about-page">
    {/* Hero band — heading + subhead + narrative opening flow directly beside the photo */}
    <section className="pt-24 pb-8 sm:pt-28 sm:pb-10">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">About Jess</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#4F5D4F] leading-[1.05] tracking-tight" data-testid="about-heading">
            Hi, I'm <em className="italic text-[#B89B63]">Jess</em>!
          </h1>
          <p className="mt-4 font-serif italic text-lg text-[#4F5D4F]/85 leading-snug max-w-xl">
            And I know what it's like to wake up one day and realize you're not sure what
            you believe anymore—or who you are without everything you were taught to be.
          </p>
          <div className="mt-5 space-y-4 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-[1.7] max-w-xl">
            <p>
              I grew up in a high-control evangelical Christian environment where I learned to
              look outside myself for the "right" answer asking questions like:
            </p>
            <ul className="font-serif italic text-lg text-[#4F5D4F] space-y-0.5 pl-1">
              <li>What does God want?</li>
              <li>What am I supposed to believe?</li>
              <li>What does the Bible say about this?</li>
              <li>What should a good Christian woman do?</li>
              <li>What if I get it wrong?</li>
            </ul>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[3/4] max-w-[280px] ml-auto">
            <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#D7B2A3]/60 z-0" />
            <img
              src={PORTRAIT}
              alt="Jess Eisaman with her husband and their two dogs"
              className="relative z-10 w-full h-full object-cover"
              data-testid="about-portrait"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Continuous narrative — starts right where the hero left off */}
    <section className="pb-16">
      <div className="max-w-2xl mx-auto px-6 space-y-4 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-[1.7]">
        <p>
          For a long time, I didn't realize how deeply that had shaped my relationship
          with my own voice.
        </p>
        <p>
          I learned to second-guess my thoughts. My feelings. My needs. My questions. Even
          my own discernment.
        </p>
        <p>
          And then my life began to change and I started to see how things didn't line up
          anymore. When my faith started unraveling, so did parts of my identity.
        </p>
        <p>I wasn't only questioning what I believed about God.</p>
        <p className="font-serif italic text-xl text-[#4F5D4F]">I was questioning me.</p>

        <ul className="font-serif italic text-lg text-[#4F5D4F]/90 space-y-0.5 pl-1">
          <li>Who am I if I don't believe everything I once believed?</li>
          <li>What do I actually think?</li>
          <li>What do I want?</li>
          <li>Can I trust myself?</li>
          <li>What does faith look like when it isn't built on fear?</li>
          <li>Who is God really? And who am I becoming now?</li>
        </ul>

        <p>I wish I could tell you I answered those questions quickly. I didn't.</p>
        <p>
          I've walked through church hurt, religious deconstruction, infertility, foster
          care trauma, the death of one of my closest friends, career uncertainty, and
          seasons where the life I had built no longer felt like it fit.
        </p>
        <p>And somewhere in all of that, something began to shift.</p>
        <p>
          I stopped trying so hard to find someone who could tell me the right way forward.
          And I started learning how to listen to myself.
        </p>
        <p>Not because I suddenly had every answer. I still don't.</p>
        <p>But I've learned that I don't need certainty to trust myself.</p>
        <p>
          That I can examine what I was taught without immediately knowing what I'll
          believe instead. That my needs, feelings, values, desires, and lived experiences matter.
          That faith can become something I choose and experience for myself rather
          than something someone else defines for me.
        </p>

        <div className="pt-4 border-l-2 border-[#B89B63] pl-5">
          <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">
            Most importantly, becoming isn't about finding some perfectly healed, finished
            version of myself. It's about giving myself permission to keep discovering who I am.
          </p>
        </div>

        <p className="pt-2">And that's why I do this work. Today, I help women do the same.</p>
        <p>
          I'm a <strong className="font-medium text-[#4F5D4F]">Faith &amp; Identity Coach</strong>,
          seminary graduate, former Christian counselor, and{" "}
          <strong className="font-medium text-[#4F5D4F]">ICF-certified coach</strong>.
        </p>
        <p>
          But I'm not here to become another authority telling you what you're supposed to
          believe or who you're supposed to be.
        </p>
        <p className="font-serif italic text-lg text-[#4F5D4F]">You've had enough of that.</p>
        <p>
          I'm here to give you a safe place to bring the questions you haven't known where
          to put. Together, we can untangle what you were taught from what you actually believe,
          explore what's yours and what was assigned to you, and make room for grief, anger,
          curiosity, uncertainty, hope—and whatever else shows up.
        </p>
        <p>And little by little, you can learn to hear and trust your own voice again.</p>

        {/* "Because I believe..." — inline, no section break */}
        <p className="pt-4">Because I believe...</p>
        <ul className="font-serif text-xl text-[#4F5D4F] leading-[1.4] space-y-1.5" data-testid="about-believe-list">
          <li>Your questions are allowed here.</li>
          <li>Your lived experience matters.</li>
          <li>You don't have to abandon yourself to be good.</li>
          <li>You don't need to have your entire faith figured out today.</li>
          <li className="italic text-[#B89B63]">You are allowed to change.</li>
          <li className="italic text-[#B89B63]">You are allowed to discover.</li>
          <li className="italic text-[#B89B63]">You are allowed to become.</li>
        </ul>
        <p>
          And you don't need me to hand you another set of answers. You need a safe place
          to discover your own.
        </p>

        {/* "This work is an invitation to..." — inline, no section break */}
        <p className="pt-4">This work is an invitation to...</p>
        <div className="space-y-3">
          {[
            { label: "Pause", copy: "the noise, urgency, and pressure to figure everything out." },
            { label: "Reclaim", copy: "your voice, needs, values, discernment, and ability to trust yourself." },
            { label: "Become", copy: "the woman you get to define—while rebuilding a faith, identity, and life that feel genuinely yours." },
          ].map((s) => (
            <p key={s.label} className="text-base sm:text-lg text-[#4F5D4F]/90 leading-relaxed">
              <span className="font-serif text-2xl sm:text-3xl text-[#B89B63] tracking-tight mr-3 align-baseline">{s.label}</span>
              <span className="font-light">{s.copy}</span>
            </p>
          ))}
        </div>

        <div className="pt-4 border-l-2 border-[#B89B63] pl-5 space-y-1">
          <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">Pause long enough to hear yourself.</p>
          <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">Reclaim what belongs to you.</p>
          <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">
            Become the woman only <em className="text-[#B89B63]">you</em> get to define.
          </p>
        </div>

        <div className="pt-6">
          <Link
            to="/#pathway"
            className="group inline-flex items-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-7 py-4 text-xs tracking-eyebrow uppercase transition-colors"
            data-testid="about-explore-cta"
          >
            Explore Ways to Work Together
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  </main>
);

export default About;
